import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ExternalLink,
  Stethoscope,
  Brain,
  CalendarClock,
  ClipboardList,
  FileText,
  Database,
  Smartphone,
  Layers,
  ShieldCheck,
  Sparkles,
  UserCircle2,
  ClipboardCheck,
  Workflow,
  Zap,
  Cloud,
  Lock,
  Microscope,
  CheckCircle2,
  Network,
  Activity,
  HeartPulse,
  Building2,
  ArrowUpRight,
  MonitorSmartphone,
} from "lucide-react";

// Reusable browser-chrome wrapper for app screenshots
const AppFrame = ({
  url,
  imgSrc,
  imgAlt,
}: {
  url: string;
  imgSrc: string;
  imgAlt: string;
}) => (
  <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_-12px_rgba(0,0,0,0.2)] border border-border/50">
    <div className="bg-[#1e1e2e] px-4 py-2.5 flex items-center gap-3">
      <div className="flex gap-1.5 shrink-0">
        <span className="block w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
        <span className="block w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
        <span className="block w-2.5 h-2.5 rounded-full bg-[#28ca41]" />
      </div>
      <div className="flex-1 mx-3 min-w-0">
        <div className="bg-white/10 rounded px-3 py-0.5 text-[10px] text-white/50 text-center truncate">
          {url}
        </div>
      </div>
    </div>
    <img src={imgSrc} alt={imgAlt} className="w-full block" />
  </div>
);

const Prismedic = () => {
  const audienceChips = [
    { icon: Stethoscope, label: "Doctors" },
    { icon: UserCircle2, label: "Medical Assistants" },
    { icon: Brain, label: "AI-Assisted" },
    { icon: Cloud, label: "Cloud Native" },
  ];

  const heroStats = [
    { value: "70%+", label: "Cases with reduced treatment time" },
    { value: "AI-First", label: "Diagnosis assistance" },
    { value: "Mobile-Ready", label: "Practice anywhere" },
    { value: "AWS-Hosted", label: "Enterprise-grade security" },
  ];

  const capabilities = [
    {
      icon: Brain,
      title: "AI-Driven Prescriptions",
      description:
        "Rich, context-sensitive prescription creation that works in coordination with patient check-in and record-taking.",
    },
    {
      icon: CalendarClock,
      title: "Slot & Schedule Management",
      description:
        "Define and publish weekly slots that automatically appear in HealthPal for patient bookings.",
    },
    {
      icon: UserCircle2,
      title: "Doctor Profile & Portfolio",
      description:
        "Build a public-facing profile and portfolio that establishes credibility and reach.",
    },
    {
      icon: ClipboardList,
      title: "Smart Booking Flow",
      description:
        "Optional patient form fill-ups and document uploads make every consultation more context-aware.",
    },
    {
      icon: FileText,
      title: "Always-Preview Templates",
      description:
        "Multiple prescription templates with real-time preview as you compose — no surprises.",
    },
    {
      icon: Database,
      title: "Exhaustive Medical Database",
      description:
        "Curated medicines and investigations library — pick, group, and prescribe with confidence.",
    },
    {
      icon: Layers,
      title: "Personal Templates & Groups",
      description:
        "Save your most-used combinations as templates and groups for lightning-fast prescriptions.",
    },
    {
      icon: Smartphone,
      title: "Mobile-First Operation",
      description:
        "Run your entire practice from a phone — generate prescriptions without a laptop in hand.",
    },
    {
      icon: ShieldCheck,
      title: "Secure on AWS",
      description:
        "Patient and clinical data hosted on AWS with enterprise-grade security and compliance.",
    },
  ];

  const bookingSteps = [
    {
      icon: UserCircle2,
      title: "Discover",
      description:
        "Patients browse the doctor's published profile, portfolio and weekly slots inside HealthPal.",
    },
    {
      icon: CalendarClock,
      title: "Book",
      description:
        "They pick a convenient slot and confirm — instantly appearing in the doctor's Prismedic schedule.",
    },
    {
      icon: ClipboardCheck,
      title: "Pre-Consult Forms",
      description:
        "Patients optionally complete intake forms and upload reports, prescriptions, or imaging.",
    },
    {
      icon: Brain,
      title: "AI Context",
      description:
        "Prismedic's AI ingests the patient's intake and history to prepare a context-rich consultation.",
    },
    {
      icon: HeartPulse,
      title: "Consult & Prescribe",
      description:
        "The doctor reviews AI-suggested treatment plans and finalizes a precise, personalized prescription.",
    },
  ];

  const aiBenefits = [
    {
      icon: Zap,
      title: "Faster diagnosis",
      description:
        "AI surfaces relevant insights so doctors reach the right diagnosis with less effort.",
    },
    {
      icon: Activity,
      title: "More accurate plans",
      description:
        "Treatment recommendations consider patient history, intake forms, and uploaded reports.",
    },
    {
      icon: ClipboardCheck,
      title: "Reduced admin load",
      description:
        "Documentation, drafting, and structuring of prescriptions are accelerated by AI.",
    },
    {
      icon: HeartPulse,
      title: "70%+ time saved",
      description:
        "For the majority of routine cases, treatment time drops significantly with AI assistance.",
    },
  ];

  const platformFeatures = [
    {
      badge: "Active Bookings",
      badgeColor: "text-primary",
      dotColor: "bg-primary",
      url: "app.prismedic.in/bookings",
      img: "/prismedic-bookings.png",
      alt: "Prismedic Active Bookings Interface",
      caption:
        "Real-time patient queue with live session tracking, check-in status, and AI-powered clinic support chat.",
    },
    {
      badge: "File Management",
      badgeColor: "text-secondary",
      dotColor: "bg-secondary",
      url: "app.prismedic.in/files",
      img: "/prismedic-files.png",
      alt: "Prismedic File Management Interface",
      caption:
        "Intelligent file storage with auto-archival, quota planning, category-based allocation, and duplicate detection.",
    },
  ];

  return (
    <Layout>
      {/* ─────────────  HERO  ───────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-secondary">
        <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-secondary/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[450px] h-[450px] rounded-full bg-cyan-400/20 blur-3xl" />
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="container mx-auto px-4 py-20 md:py-28 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6 animate-fade-in">
                <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
                <span className="text-xs font-medium text-white tracking-wide uppercase">
                  A Wellnext Technologies Product
                </span>
              </div>

              <div className="flex items-center gap-4 mb-6 animate-fade-in delay-100">
                <div className="bg-white rounded-xl p-1 shadow-elegant flex items-center gap-1 h-20">
                  <img
                    src="/pm_logo_detailed_graphic.png"
                    alt="Prismedic"
                    className="h-16 md:h-16 w-auto"
                  />
                  <img
                    src="/Prismedic_logo.png"
                    alt="Prismedic"
                    className="h-16 md:h-16 w-auto"
                  />
                </div>
              </div>

              <h1
                className="text-4xl md:text-6xl font-bold text-white mb-5 leading-[1.05] tracking-tight animate-fade-in delay-200"
                data-testid="text-prismedic-hero-title"
              >
                A Doctor's{" "}
                <span className="bg-gradient-to-r from-cyan-200 to-white bg-clip-text text-transparent">
                  Expert AI Assistant
                </span>
              </h1>

              <p className="text-lg md:text-xl text-white/85 mb-8 max-w-2xl leading-relaxed animate-fade-in delay-300">
                Prismedic is a SaaS platform purpose-built for doctors and
                medical assistants — from prescription creation and slot
                management to a public portfolio. Far more than a prescription
                tool: it's an intelligent partner in every consultation.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in delay-500">
                <Button
                  asChild
                  size="lg"
                  className="text-base h-12 px-7 bg-white text-primary hover:bg-white/90 shadow-lg"
                  data-testid="button-launch-prismedic"
                >
                  <a
                    href="https://app.prismedic.in"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Launch Prismedic
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="text-base h-12 px-7 border-white/40 bg-white/5 backdrop-blur-md text-white hover:bg-white hover:text-primary"
                  data-testid="button-back-to-products"
                >
                  <Link to="/products">
                    All Products
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>

              <div className="mt-9 flex flex-wrap gap-x-5 gap-y-3 animate-fade-in delay-500">
                {audienceChips.map((chip) => {
                  const Icon = chip.icon;
                  return (
                    <div
                      key={chip.label}
                      className="flex items-center gap-2 text-white/85 text-sm"
                      data-testid={`chip-audience-${chip.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Icon className="h-4 w-4 text-cyan-200" />
                      {chip.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Actual prescription-writing screenshot */}
            <div className="lg:col-span-6">
              <div className="relative animate-fade-in delay-300">
                <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-cyan-300/25 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-8 -left-8 w-48 h-48 rounded-full bg-white/15 blur-3xl pointer-events-none" />

                {/* Browser frame */}
                <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_90px_-15px_rgba(0,0,0,0.5)] border border-white/20">
                  <div className="bg-[#1a1a2e] px-4 py-2.5 flex items-center gap-3">
                    <div className="flex gap-1.5 shrink-0">
                      <span className="block w-3 h-3 rounded-full bg-[#ff5f57]" />
                      <span className="block w-3 h-3 rounded-full bg-[#ffbd2e]" />
                      <span className="block w-3 h-3 rounded-full bg-[#28ca41]" />
                    </div>
                    <div className="flex-1 mx-2 min-w-0">
                      <div className="bg-white/10 rounded px-3 py-1 text-[11px] text-white/50 text-center truncate">
                        app.prismedic.in · Rx – Prescription Writing
                      </div>
                    </div>
                  </div>
                  <img
                    src="/prismedic-rx.png"
                    alt="Prismedic Prescription Writing with live PDF preview"
                    className="w-full block"
                  />
                </div>

                {/* Floating AI badge */}
                <div className="absolute -bottom-5 left-5 flex items-center gap-2 bg-white rounded-xl shadow-xl px-4 py-2 border border-border/40">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs font-semibold text-foreground">
                    AI Active · Always-Preview Mode
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Hero stats strip */}
          <div className="mt-20 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/15 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
              {heroStats.map((s) => (
                <div
                  key={s.label}
                  className="px-4 py-5 text-center"
                  data-testid={`stat-prismedic-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="text-xl md:text-2xl font-bold text-white">
                    {s.value}
                  </div>
                  <div className="text-xs md:text-sm text-white/70 mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────  CAPABILITIES  ───────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-semibold uppercase tracking-wider mb-4">
              <Sparkles className="h-3.5 w-3.5" />
              What Prismedic Does
            </div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-5">
              Everything a Modern{" "}
              <span className="text-gradient-primary">Practice Needs</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              An expert assistant for clinicians — built around your workflow,
              not the other way around.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <Card
                  key={cap.title}
                  className="group relative border-border/60 transition-all duration-300 overflow-hidden bg-gradient-to-b from-card to-card hover:to-muted/30 shadow-card hover:shadow-card-hover hover:-translate-y-1"
                  data-testid={`card-capability-${cap.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary via-secondary to-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  <CardContent className="p-7">
                    <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-5 shadow-lg shadow-primary/20 group-hover:scale-105 transition-transform">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2.5 text-foreground">
                      {cap.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-[15px]">
                      {cap.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────  AI PRESCRIPTION DEEP-DIVE  ───────────── */}
      <section className="py-24 bg-gradient-to-br from-primary/[0.04] via-secondary/[0.04] to-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 bg-grid pointer-events-none" />

        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
                <Brain className="h-3.5 w-3.5" />
                AI Prescription Engine
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight tracking-tight">
                A prescription engine{" "}
                <span className="text-gradient-primary">
                  that thinks alongside you
                </span>
              </h2>
              <p className="text-[17px] leading-relaxed text-muted-foreground mb-5">
                Prismedic's AI doesn't just autofill — it builds context from
                the patient's check-in, intake forms and uploaded records, and
                proposes treatment plans grounded in clinical relevance.
              </p>
              <p className="text-[17px] leading-relaxed text-muted-foreground mb-8">
                Every prescription stays in <strong>always-preview</strong>{" "}
                mode, so you see exactly how the patient will receive it. Pick
                from multiple templates, edit on the fly, and rely on a curated
                medicine and investigation database.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aiBenefits.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div
                      key={b.title}
                      className="flex gap-3 p-4 rounded-xl border border-border/60 bg-card shadow-card"
                      data-testid={`item-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="h-10 w-10 shrink-0 rounded-lg bg-gradient-to-br from-primary to-secondary grid place-items-center">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground text-sm">
                          {b.title}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                          {b.description}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Visual: Template panel */}
            <div className="relative">
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-secondary/20 blur-3xl" />
              <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-primary/20 blur-3xl" />

              <div className="relative rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
                <div className="p-5 border-b border-border bg-muted/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                    <div className="text-sm font-semibold">Live Preview</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {["General OPD", "Pediatric", "ENT", "Custom"].map(
                      (t, i) => (
                        <span
                          key={t}
                          className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${
                            i === 0
                              ? "bg-primary text-white"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {t}
                        </span>
                      )
                    )}
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  <div className="rounded-lg p-4 bg-gradient-to-br from-primary/5 to-secondary/5 border border-border/60">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-3.5 w-3.5 text-secondary" />
                      <div className="text-xs font-semibold text-secondary uppercase tracking-wider">
                        AI suggestion
                      </div>
                    </div>
                    <div className="text-sm text-foreground/90">
                      Based on intake + uploaded CBC report, consider
                      broad-spectrum antibiotic with anti-pyretic for
                      symptomatic relief.
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                      Saved Groups
                    </div>
                    <div className="space-y-2">
                      {[
                        { label: "Acute pharyngitis", count: "5 items" },
                        { label: "Diabetes follow-up", count: "8 items" },
                        { label: "Hypertension review", count: "6 items" },
                      ].map((g) => (
                        <div
                          key={g.label}
                          className="flex items-center justify-between rounded-lg border border-border p-3 hover:bg-muted/40 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Layers className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium text-foreground">
                              {g.label}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {g.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                    <Database className="h-3.5 w-3.5 text-secondary" />
                    Backed by an exhaustive medicine & investigation database
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────  PATIENT BOOKING FLOW  ───────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider mb-4">
              <Workflow className="h-3.5 w-3.5" />
              Connected Booking
            </div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-5">
              From doctor's slot to{" "}
              <span className="text-gradient-primary">
                AI-context-rich consultation
              </span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Doctors define schedules in Prismedic; patients book seamlessly
              through HealthPal. Every step adds context that makes AI
              assistance more accurate.
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {bookingSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.title}
                    className="relative"
                    data-testid={`step-booking-${i + 1}`}
                  >
                    <div className="bg-card border border-border/60 rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow h-full">
                      <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary grid place-items-center mb-4 shadow-md shadow-primary/20">
                        <Icon className="h-6 w-6 text-white" />
                        <span className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-white text-primary text-[11px] font-bold grid place-items-center border border-border shadow-sm">
                          {i + 1}
                        </span>
                      </div>
                      <h3 className="font-semibold text-foreground mb-1.5">
                        {step.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-12 max-w-3xl mx-auto rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 to-secondary/5 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-12 w-12 shrink-0 rounded-xl bg-gradient-to-br from-primary to-secondary grid place-items-center">
              <CheckCircle2 className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-foreground">
                Optional, never blocking
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Patients can skip forms and uploads — but every detail they
                share feeds Prismedic's AI to deliver a sharper, faster
                diagnosis at the visit.
              </p>
            </div>
          </div>

          {/* Booking interface screenshot */}
          <div className="mt-16">
            <div className="flex items-center gap-2 mb-5 justify-center">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                Live Booking Dashboard
              </span>
            </div>
            <div className="max-w-5xl mx-auto">
              <AppFrame
                url="app.prismedic.in/bookings · Active Bookings"
                imgSrc="/prismedic-bookings.png"
                imgAlt="Prismedic Active Bookings Interface showing real-time patient queue"
              />
              <p className="mt-4 text-sm text-muted-foreground text-center">
                The live booking dashboard shows all sessions, patient queue,
                check-in status, paid confirmations, and AI-powered clinic support — all in one view.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────  PLATFORM IN ACTION  ───────────── */}
      <section className="py-24 bg-gradient-to-b from-muted/40 via-muted/20 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
              <MonitorSmartphone className="h-3.5 w-3.5" />
              Platform in Action
            </div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-5">
              Every corner of your practice,{" "}
              <span className="text-gradient-primary">covered</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              From file management to financial tracking — a truly complete
              clinical management platform, not just a prescription tool.
            </p>
          </div>

          {/* Files + Bookings screenshots 2-col */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {platformFeatures.map((f) => (
              <div key={f.badge}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`h-2 w-2 rounded-full ${f.dotColor}`} />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${f.badgeColor}`}>
                    {f.badge}
                  </span>
                </div>
                <AppFrame url={f.url} imgSrc={f.img} imgAlt={f.alt} />
                <p className="mt-3 text-sm text-muted-foreground">{f.caption}</p>
              </div>
            ))}
          </div>

          {/* Invoices — full-width */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="h-2 w-2 rounded-full bg-accent" />
              <span className="text-xs font-semibold text-accent uppercase tracking-wider">
                Finance &amp; Invoices
              </span>
            </div>
            <AppFrame
              url="app.prismedic.in/finance/invoices"
              imgSrc="/prismedic-invoices.png"
              imgAlt="Prismedic Invoice Management Interface"
            />
            <p className="mt-3 text-sm text-muted-foreground">
              Full billing suite — create invoices, track payment gateway
              transactions, manage vouchers, and maintain clean financial
              records across all your clinics.
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────  MOBILE  +  ECOSYSTEM  ───────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Mobile-first card with HealthPal photo */}
            <Card className="border-border/60 shadow-card overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img
                  src="/prismedic-mobile.jpg"
                  alt="HealthPal patient mobile app on a phone"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
                <div className="absolute bottom-4 left-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/90 text-white text-xs font-semibold uppercase tracking-wider">
                    <Smartphone className="h-3.5 w-3.5" />
                    Mobile-First
                  </div>
                </div>
              </div>
              <CardContent className="p-8 pt-6">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
                  Your entire practice — in your pocket
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The Prismedic mobile app puts every feature on your phone.
                  Generate prescriptions, manage your slots, view patient
                  records and run your day — without ever needing a laptop.
                </p>
                <ul className="space-y-3">
                  {[
                    "Compose & deliver prescriptions on the go",
                    "Live slot updates and patient queue",
                    "Voice & quick-template support",
                    "Works seamlessly with the web version",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm text-foreground/85"
                    >
                      <CheckCircle2 className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Vitalink integration */}
            <Card className="relative border-border/60 shadow-card overflow-hidden bg-gradient-to-br from-primary to-secondary text-white">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-cyan-300/20 blur-3xl" />
              <CardContent className="p-8 md:p-10 relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-semibold uppercase tracking-wider mb-4">
                  <Network className="h-3.5 w-3.5" />
                  Connected Ecosystem
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
                  Works seamlessly with{" "}
                  <span className="bg-gradient-to-r from-cyan-200 to-white bg-clip-text text-transparent">
                    Vitalink
                  </span>
                </h3>
                <p className="text-white/85 leading-relaxed mb-6">
                  Prismedic plugs directly into <strong>Vitalink</strong>, our
                  enterprise platform for clinics, diagnostic centers, labs and
                  hospitals — so your individual practice fits effortlessly into
                  a larger care delivery network.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Building2, label: "Clinics" },
                    { icon: Microscope, label: "Diagnostics" },
                    { icon: Activity, label: "Labs" },
                    { icon: HeartPulse, label: "Hospitals" },
                  ].map((p) => {
                    const Icon = p.icon;
                    return (
                      <div
                        key={p.label}
                        className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-white/10 backdrop-blur border border-white/15"
                      >
                        <Icon className="h-4 w-4 text-cyan-200" />
                        <span className="text-sm font-medium text-white">
                          {p.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <a
                  href="https://www.vitalink.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-100 hover:text-white transition-colors"
                  data-testid="link-vitalink"
                >
                  Explore Vitalink
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ─────────────  SECURITY  ───────────── */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto rounded-2xl border border-border/60 bg-gradient-to-br from-card to-muted/30 p-8 md:p-12 shadow-card">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-2 flex justify-center">
                <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-primary to-secondary grid place-items-center shadow-lg shadow-primary/20">
                  <Lock className="h-10 w-10 text-white" />
                </div>
              </div>
              <div className="md:col-span-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-3">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Trust & Security
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">
                  Patient data, hosted securely on AWS
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Prismedic's data is maintained on Amazon Web Services with
                  enterprise-grade security, encryption at rest and in transit,
                  and best-in-class access controls — keeping clinical and
                  patient information private, resilient and risk-free.
                </p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                  {[
                    "AWS-hosted",
                    "Encrypted in transit & at rest",
                    "Granular access controls",
                    "Audited infrastructure",
                  ].map((tag) => (
                    <span key={tag} className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-secondary" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────  CTA  ───────────── */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-primary shadow-elegant">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-secondary/30 blur-3xl" />
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />

            <div className="relative px-6 py-20 md:px-16 md:py-24 text-center">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight tracking-tight">
                Practice smarter with Prismedic
              </h2>
              <p className="text-lg md:text-xl text-white/85 mb-9 max-w-2xl mx-auto leading-relaxed">
                Spend less time on paperwork and more time with patients. Let AI
                carry the load — you stay in charge of every clinical decision.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  asChild
                  size="lg"
                  className="text-base h-12 px-7 bg-white text-primary hover:bg-white/90 shadow-lg"
                  data-testid="button-cta-launch-prismedic"
                >
                  <a
                    href="https://app.prismedic.in"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Launch Prismedic
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="text-base h-12 px-7 border-white/40 bg-white/5 backdrop-blur-md text-white hover:bg-white hover:text-primary"
                  data-testid="button-cta-back-products"
                >
                  <Link to="/products">Back to Products</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Prismedic;
